import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import { SEO } from "@/components/common/SEO";
import { PageHero } from "@/components/common/PageHero";
import { ContactCta } from "@/components/common/DetailPage";
import { imageFor } from "@/lib/imagery";
import { COMPANY } from "@/data/company";
import type { RouteKey } from "@/lib/routes";

const RELATED: RouteKey[] = ["compliance.corporate", "compliance.antifraud"];

interface Row {
  label: string;
  value: string;
  href?: string;
}

function contentFor(locale: "en" | "it") {
  const en = locale === "en";

  const identifiers: Row[] = [
    { label: en ? "Legal name" : "Denominazione", value: COMPANY.legalName },
    { label: en ? "Legal form" : "Forma giuridica", value: COMPANY.legalForm },
    { label: en ? "Country" : "Paese", value: COMPANY.address.country[locale] },
    { label: "P. IVA", value: COMPANY.vat },
    { label: en ? "Tax Code (Codice Fiscale)" : "Codice Fiscale", value: COMPANY.taxCode },
    { label: "REA", value: COMPANY.rea },
    { label: en ? "Registered with" : "Iscritta presso", value: COMPANY.registeredWith },
    { label: en ? "Share capital" : "Capitale sociale", value: COMPANY.shareCapital[locale] },
    { label: en ? "EU EORI" : "EORI UE", value: COMPANY.eoriEU },
    { label: en ? "UK EORI" : "EORI Regno Unito", value: COMPANY.eoriUK },
  ];

  const channels: Row[] = [
    { label: en ? "Website" : "Sito web", value: COMPANY.websiteLabel, href: COMPANY.website },
    { label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { label: "PEC", value: COMPANY.pec, href: `mailto:${COMPANY.pec}` },
    { label: en ? "Telephone" : "Telefono", value: COMPANY.phone, href: COMPANY.phoneHref },
  ];

  const steps = en
    ? [
        "Confirm BNM's registration and REA number (PI-258592) through the Italian Business Register (Registro Imprese / Camera di Commercio Toscana Nord-Ovest).",
        "Validate the VAT number (06654120481) through the European Commission VIES system.",
        "Confirm the EU EORI (IT06654120481) and UK EORI (GB047109919000) through the respective official EORI validation services.",
        "Note that our certified email (PEC), bnmsrl@arubapec.it, carries legal validity in Italy.",
        "If any detail does not match, contact us through the official channels below before proceeding.",
      ]
    : [
        "Verifica l'iscrizione e il numero REA (PI-258592) tramite il Registro Imprese (Camera di Commercio Toscana Nord-Ovest).",
        "Convalida la partita IVA (06654120481) tramite il sistema VIES della Commissione Europea.",
        "Verifica l'EORI UE (IT06654120481) e l'EORI del Regno Unito (GB047109919000) tramite i rispettivi servizi ufficiali.",
        "La nostra PEC, bnmsrl@arubapec.it, ha valore legale in Italia.",
        "In caso di difformità, contattaci tramite i canali ufficiali indicati di seguito prima di procedere.",
      ];

  return {
    labels: {
      overview: en ? "Overview" : "Panoramica",
      identifiers: en ? "Official identifiers" : "Identificativi ufficiali",
      steps: en ? "How to verify us" : "Come verificarci",
      channels: en ? "Official channels" : "Canali ufficiali",
    },
    overview: en
      ? "Before entering a commercial relationship, any counterparty should confirm that BNM S.r.l. is a registered and active company. BNM publishes its official identifiers so that buyers, suppliers and banks can verify them directly through independent public sources, rather than relying on documents alone."
      : "Prima di avviare un rapporto commerciale, ogni controparte dovrebbe verificare che BNM S.r.l. sia una società registrata e attiva. BNM pubblica i propri identificativi ufficiali affinché buyer, fornitori e banche possano verificarli direttamente tramite fonti pubbliche indipendenti, senza affidarsi ai soli documenti.",
    identifiers,
    steps,
    channels,
    disclaimer: en
      ? "Where third-party information conflicts with the official records of the Camera di Commercio Toscana Nord-Ovest or the channels listed here, the official public records prevail."
      : "In caso di difformità rispetto ai registri ufficiali della Camera di Commercio Toscana Nord-Ovest o ai canali indicati, prevalgono i registri pubblici ufficiali.",
  };
}

function DataRows({ rows }: { rows: Row[] }) {
  return (
    <dl className="flex flex-col">
      {rows.map((row) => (
        <div
          key={row.label}
          className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6 border-b border-bone-500 py-3"
        >
          <dt className="font-mono text-[11px] uppercase tracking-eyebrow text-slate2 shrink-0">
            {row.label}
          </dt>
          <dd className="text-ink text-sm sm:text-right">
            {row.href ? (
              <a
                href={row.href}
                className="hover:text-clay transition-colors border-b border-transparent hover:border-clay"
                {...(row.href.startsWith("http")
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                {row.value}
              </a>
            ) : (
              row.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function Kyc() {
  const { t, path, locale } = useT();
  const img = imageFor("compliance.kyc");
  const c = contentFor(locale);

  return (
    <>
      <SEO routeKey="compliance.kyc" />
      <PageHero routeKey="compliance.kyc" imageUrl={img?.url} imageAlt={img?.alt} />

      <section className="container-bnm py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <p className="eyebrow mb-4">{t.sections.scopeLabel}</p>
            <p className="text-slate2 max-w-prose-tight leading-relaxed">
              {(t.routes["compliance.kyc"] as { lede?: string }).lede}
            </p>
          </aside>

          <div className="lg:col-span-8 flex flex-col gap-12">
            <div data-testid="kyc-overview">
              <p className="eyebrow mb-4">{c.labels.overview}</p>
              <p className="text-ink text-lg md:text-xl font-display font-light leading-snug max-w-prose">
                {c.overview}
              </p>
            </div>

            <div data-testid="kyc-identifiers">
              <p className="eyebrow mb-5">{c.labels.identifiers}</p>
              <DataRows rows={c.identifiers} />
            </div>

            <div data-testid="kyc-steps">
              <p className="eyebrow mb-5">{c.labels.steps}</p>
              <ol className="flex flex-col border-t border-bone-500">
                {c.steps.map((step, idx) => (
                  <li
                    key={idx}
                    className="grid grid-cols-12 gap-4 md:gap-8 border-b border-bone-500 py-5"
                  >
                    <span className="col-span-2 md:col-span-1 font-mono text-[11px] uppercase tracking-eyebrow text-slate2 pt-1">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <p className="col-span-10 md:col-span-11 text-slate2 leading-relaxed text-[15px]">
                      {step}
                    </p>
                  </li>
                ))}
              </ol>
            </div>

            <div data-testid="kyc-channels">
              <p className="eyebrow mb-5">{c.labels.channels}</p>
              <DataRows rows={c.channels} />
            </div>

            <div className="border-l-2 border-bone-500 pl-6 py-2" data-testid="kyc-disclaimer" role="note">
              <p className="text-sm text-slate2 leading-relaxed max-w-prose">{c.disclaimer}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-bone-500 bg-bone-300/40">
        <div className="container-bnm py-16 md:py-20">
          <p className="eyebrow mb-8">{t.sections.seeAlso}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {RELATED.map((k) => (
              <Link
                key={k}
                to={path(k)}
                className="group border border-bone-500 bg-bone p-6 md:p-8 hover:border-ink transition-colors"
                data-testid={`related-${k.replace(".", "-")}`}
              >
                <p className="eyebrow mb-4">{(t.routes[k] as { eyebrow?: string }).eyebrow}</p>
                <h3 className="font-display text-xl md:text-2xl text-ink group-hover:text-clay transition-colors">
                  {t.routes[k].label}
                </h3>
                <span className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-eyebrow text-slate2 group-hover:text-clay transition-colors">
                  <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactCta />
    </>
  );
}
